import type { TubeSpec, TubeState, Vec2, Vec3 } from '../types';

const TMP: Vec3 = { x: 0, y: 0, z: 0 };

/** Unit direction from an anchor toward the hanging end, from tangent-slope lean. */
export function hangDir(lean: Vec2, out: Vec3 = { x: 0, y: 0, z: 0 }): Vec3 {
  const lx = lean.x, lz = lean.y;
  const inv = 1 / Math.sqrt(1 + lx * lx + lz * lz);
  out.x = lx * inv;
  out.y = -inv;
  out.z = lz * inv;
  return out;
}

/** Point at distance `dist` from `anchor` along the hanging line described by `lean`. */
export function pointAlongHang(anchor: Vec3, lean: Vec2, dist: number, out: Vec3 = { x: 0, y: 0, z: 0 }): Vec3 {
  const d = hangDir(lean, TMP);
  out.x = anchor.x + d.x * dist;
  out.y = anchor.y + d.y * dist;
  out.z = anchor.z + d.z * dist;
  return out;
}

/** World position of the point at parameter s (0 = top of tube, 1 = bottom) on a tube. */
export function tubePointOf(spec: TubeSpec, state: TubeState, s: number, out: Vec3 = { x: 0, y: 0, z: 0 }): Vec3 {
  return pointAlongHang(spec.anchor, state.lean, spec.cord + s * spec.length, out);
}

/** Both ends of a tube in one call. */
export function tubeEnds(spec: TubeSpec, state: TubeState, top: Vec3, bottom: Vec3): void {
  pointAlongHang(spec.anchor, state.lean, spec.cord, top);
  pointAlongHang(spec.anchor, state.lean, spec.cord + spec.length, bottom);
}

/**
 * A free-free bar's overtones are not harmonic. Ratios relative to the fundamental, from
 * Euler-Bernoulli beam theory: 1 : 2.756 : 5.404 : 8.933 : 13.34 : 18.64.
 * Shared here so audio and any visual spectral cue agree.
 */
export const BAR_PARTIALS: readonly number[] = [1, 2.756, 5.404, 8.933, 13.344, 18.638];

/** Visual tube length (m) from fundamental frequency: f is proportional to 1/L^2 for a bar. */
export function lengthForFrequency(hz: number, k = 11.4): number {
  return k / Math.sqrt(hz);
}
